// ============================================================
// staleChunk.ts - 재배포로 죽은 동적 청크(lazy chunk) 복구
// ============================================================
//
// 빌드마다 asset 파일명 해시가 바뀌고 옛 파일은 지워진다. 그래서 배포 전부터
// 열어둔 탭은 이미 사라진 청크를 계속 가리킨다 — 그 탭에서 XLSX 내보내기처럼
// 동적 import를 처음 건드리는 순간에만 404가 터진다. 코드 결함이 아니라
// "화면이 낡았다"는 신호이므로, 사용자에게는 새로고침 한 번으로 끝나는 일이다.

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
];

/** 동적 청크를 못 받아온 실패인가 — 즉, 화면이 낡아서 난 실패인가. */
export const isStaleChunkError = (err: unknown): boolean => {
  const message = err instanceof Error ? err.message : String(err);
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

export const STALE_CHUNK_MESSAGE =
  '새 버전이 배포되어 지금 열린 화면이 낡았습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.';

// 새로고침해도 같은 실패가 나면(서버 asset 자체가 빠진 경우) 무한 새로고침이
// 된다. 한 번 새로고침했다는 사실을 세션에 남겨 두 번째부터는 안내만 한다.
const RELOAD_MARK = 'innohomes:staleChunkReloaded';

const alreadyReloaded = (): boolean => {
  try {
    return sessionStorage.getItem(RELOAD_MARK) === '1';
  } catch {
    return false;
  }
};

const markReloaded = () => {
  try {
    sessionStorage.setItem(RELOAD_MARK, '1');
  } catch {
    // 저장을 막아둔 브라우저 — 새로고침 자체는 그대로 진행한다.
  }
};

/**
 * 낡은 화면이면 한 번만 자동 새로고침한다.
 *
 * @returns 새로고침을 걸었으면 true. 부르는 쪽은 true일 때 별도 에러 안내를
 *          띄우지 않아도 된다(어차피 화면이 갈아엎어진다).
 */
export const reloadIfStaleChunk = (err: unknown): boolean => {
  if (!isStaleChunkError(err) || alreadyReloaded()) return false;
  markReloaded();
  window.location.reload();
  return true;
};

/** 정상적으로 살아난 화면에서는 표식을 지워 다음 배포 때 다시 자동 복구된다. */
export const clearStaleChunkMark = () => {
  try {
    sessionStorage.removeItem(RELOAD_MARK);
  } catch {
    // 무시 — 표식이 남아도 안내 문구로 대체된다.
  }
};
